import { RecurrenceSummary } from '@/types/recurrence';
import { SERVICE_FREQUENCY } from '@/types/service-frequency';
import { formatDate, formatTime } from './format-date';
import { formatQuota } from './format-quota';

const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

type Schedule = NonNullable<RecurrenceSummary['serviceSchedules']>[number];

/** "Seg, Qua · 14:00" — o que o cliente montou em uma grade da recorrência. */
export function recurrenceScheduleLine(schedule: Schedule): string {
  const when = (() => {
    switch (schedule.frequency) {
      case SERVICE_FREQUENCY.DAILY:
        return 'Todo dia';
      case SERVICE_FREQUENCY.MONTHLY:
        return schedule.dayOfMonth ? `Todo dia ${schedule.dayOfMonth}` : 'Mensal';
      case SERVICE_FREQUENCY.WEEKLY: {
        const days = (schedule.daysOfWeek ?? [])
          .map((d) => WEEKDAY_SHORT[d])
          .filter(Boolean);
        return days.length ? days.join(', ') : 'Semanal';
      }
      default:
        return schedule.frequency;
    }
  })();

  if (!schedule.startTime) return when;
  const time = schedule.endTime
    ? `${formatTime(schedule.startTime)}–${formatTime(schedule.endTime)}`
    : formatTime(schedule.startTime);
  return `${when} · ${time}`;
}

/**
 * O que o contrato entrega, uma linha por informação.
 *
 * A assinatura abre pelo que ela promete — o saldo ("3 sessões por semana") —
 * e só então lista os horários que o cliente deixou pré-marcados, se deixou:
 * eles saem dessa mesma cota, e quem não montou nada marca sessão a sessão.
 */
export function recurrenceScheduleLines(item: RecurrenceSummary): string[] {
  const grade = (item.serviceSchedules ?? []).map(recurrenceScheduleLine);
  if (item.quotaPerPeriod && item.quotaPeriod) {
    return [formatQuota(item.quotaPerPeriod, item.quotaPeriod), ...grade];
  }
  return grade;
}

/** Resumo em uma linha só — para banner/preview compacto. */
export function recurrenceScheduleSummary(item: RecurrenceSummary): string {
  const lines = recurrenceScheduleLines(item);
  if (lines.length === 0) return 'Agenda recorrente';
  return lines.join(' · ');
}

/** "A partir de 12/08" ou "12/08 até 12/11". */
export function recurrencePeriodLabel(item: RecurrenceSummary): string {
  const start = formatDate(item.startDate, 'dd/MM');
  if (!item.endDate) return `A partir de ${start}`;
  return `${start} até ${formatDate(item.endDate, 'dd/MM')}`;
}
